import React, { useState, useEffect } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomTextField from "../../components/custom-text-field/CustomTextField";
import CustomDropdown from "../../components/custom-dropdown/CustomDropdown";
import CustomPointsRange from "../../components/custom-points-range/CustomPointsRange";
import CustomButton from "../custom-button/CustomButton";
import {
  BTN_CANCEL,
  BTN_SAVE_DRAFT,
  BTN_SAVE_MILESTONE,
  BTN_SAVE_UPDATE,
  FORM_LABEL_CODE,
  FORM_LABEL_CONTENT_TYPE,
  FORM_LABEL_DESCRIPTION,
  FORM_LABEL_LEARNING_CONTENT_ID,
  FORM_LABEL_NAME,
  FORM_LABEL_POINTS_RANGE,
  FORM_LABEL_STATUS,
  FORM_LABEL_TRIMESTERORGENERAL,
  FORM_LABEL_TYPE,
  FORM_LABEL_URL_LINK,
  FORM_PLACEHOLDER_EDUCATION_DESCRIPTION,
  FORM_PLACEHOLDER_MILESTONE_TYPE_DROPDOWN,
  FORM_PLACEHOLDER_TEXTFIELD,
  FORM_PLACEHOLDER_TRIMESTER_DROPDOWN,
  MULTI_LANGUAGE_SUPPORT,
  MULTI_LANGUAGE_SUPPORT_NOTE,
} from "../../utility/constants/locale";
import {
  formatContentTypes,
  formatEducationMilestoneData,
  formatEducationTaskTypes,
  getDropdownDataForCategory,
  getIdFromEducationContentType,
  getStatusIDFromStatusName,
  segregateCategoryWiseStatus,
} from "../../utility/functions/common-functions";
import { DisplayGroup } from "../../utility/interfaces/masterDataInterfaces";
import VerifyLink from "./VerifyLink";
import { useAppSelector } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  CATEGORY_ID_MAPPING,
  MILESTONE_MESSAGES,
  PATIENTTASK_STATUS_ID_MAPPING,
  REGEX_VALIDATIONS,
} from "../../utility/constants/common-constants";
import MultiLanguageForm from "../multi-language-form/MultiLanguageForm";
import LanguagePills from "../language-pills/LanguagePills";
import ConfirmationModal from "../multi-language-form/ConfirmationModal";

interface EducationContentFormProps {
  initialValues: {
    sourceMilestoneCode: string;
    milestoneTypeId: number;
    milestoneDisplayName: string;
    rewardPoints: number;
    displayGroupId: number;
    statusId: number;
    educationalContentTypeId: number;
    description: string;
    milestoneLearningUrls: string;
  };
  translations?: {
    milestoneTranslationId: number;
    milestoneId: number;
    languageId: number;
    displayName: string;
    longDescription: string;
  }[];
  languageIds?: number[];
  onSubmit: (values: any) => void;
  onSaveDraft?: (values: any) => void;
  isEditMode?: boolean;
  isMilestoneInUse?: boolean;
  milestoneData: DisplayGroup[];
}

const EducationContentForm: React.FC<EducationContentFormProps> = ({
  initialValues,
  onSubmit,
  onSaveDraft,
  isEditMode = false,
  isMilestoneInUse,
  milestoneData,
  languageIds,
  translations,
}) => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(initialValues);
  const [multiLanguageValues, setMultiLanguageValues] = useState<{
    [key: number]: { name: string; description: string };
  }>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<number[]>([]);
  const [isLinkVerified, setIsLinkVerified] = useState(false);
  const categoryWiseStatus = useAppSelector(
    (state) => state.adminMasterData.categoryWiseStatus
  );
  const contentTypes = useAppSelector(
    (state) => state.adminMasterData.educationalContentTypes
  );
  const activeLanguages = useAppSelector(
    (state) => state.adminMasterData.activeLanguages
  );
  const taskTypes = useAppSelector((state) => state.adminMasterData.taskTypes);
  const draftStatusId = getStatusIDFromStatusName(
    CATEGORY_ID_MAPPING.patientTask,
    PATIENTTASK_STATUS_ID_MAPPING.draft,
    categoryWiseStatus
  );

  const validateForm = () => {
    const requiredFields: (keyof typeof formValues)[] = [
      "milestoneTypeId",
      "milestoneDisplayName",
      "sourceMilestoneCode",
      "rewardPoints",
      "displayGroupId",
    ];

    const areRequiredFieldsValid = requiredFields.every(
      (field) => !!formValues[field]
    );

    const isContentValid =
      formValues.educationalContentTypeId ===
      getIdFromEducationContentType(contentTypes, "URL")
        ? !!formValues.milestoneLearningUrls
        : !!formValues.sourceMilestoneCode;

    return areRequiredFieldsValid && isContentValid;
  };

  useEffect(() => {
    setFormValues(initialValues);
    if (translations && translations.length > 0) {
      const mappedTranslations = translations.reduce((acc, translation) => {
        acc[translation.languageId] = {
          name: translation.displayName,
          description: translation.longDescription,
        };
        return acc;
      }, {} as { [key: number]: { name: string; description: string } });
      setMultiLanguageValues(mappedTranslations);
    }

    if (languageIds && languageIds.length > 0) {
      setSelectedLanguageIds(languageIds);
    }
  }, [initialValues, translations, languageIds]);

  useEffect(() => {
    setIsLinkVerified(false);
  }, [
    formValues.educationalContentTypeId,
    formValues.milestoneLearningUrls,
    formValues.sourceMilestoneCode,
  ]);

  const handleInputChange = (name: string, value: string | number) => {
    setFormValues({ ...formValues, [name]: value });

    if (name === "milestoneDisplayName") {
      const languageId = activeLanguages.find(
        (lang) => lang.languageId === 1
      )?.languageId;

      if (languageId && value) {
        setSelectedLanguageIds((prevIds) =>
          prevIds.includes(languageId) ? prevIds : [...prevIds, languageId]
        );
      } else if (languageId && !value) {
        setSelectedLanguageIds((prevIds) =>
          prevIds.filter((id) => id !== languageId)
        );
      }
    }
  };

  const handleSaveMilestoneClick = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isLinkVerified) {
      enqueueSnackbar(MILESTONE_MESSAGES.Validation.LinkNotVerified, {
        variant: "warning",
      });
    } else if (validateForm()) {
      setModalOpen(true);
    } else {
      enqueueSnackbar(MILESTONE_MESSAGES.Validation.RequiredFields, {
        variant: "warning",
      });
    }
  };

  const handleSubmit = () => {
    const translations = Object.entries(multiLanguageValues).map(
      ([languageId, values]) => ({
        languageId: Number(languageId),
        name: values.name,
        description: values.description,
      })
    );

    onSubmit({ ...formValues, translations });
    setModalOpen(false);
  };

  const handleSaveDraft = () => {
    const translations = Object.entries(multiLanguageValues).map(
      ([languageId, values]) => ({
        languageId: Number(languageId),
        name: values.name,
        description: values.description,
      })
    );
    if (!isLinkVerified) {
      enqueueSnackbar(MILESTONE_MESSAGES.Validation.LinkNotVerified, {
        variant: "warning",
      });
    } else if (validateForm()) {
      if (onSaveDraft) {
        onSaveDraft({ ...formValues, translations });
      }
    } else {
      enqueueSnackbar(MILESTONE_MESSAGES.Validation.RequiredFields, {
        variant: "warning",
      });
    }
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    navigate("/admin");
  };

  const handleContentVerified = (status: boolean) => {
    setIsLinkVerified(status);
  };

  const groupedOptions = formatEducationMilestoneData(milestoneData);
  const formattedTaskTypes = formatEducationTaskTypes(taskTypes);
  const formattedContentType = formatContentTypes(contentTypes);

  const groupedCategoryStatus = segregateCategoryWiseStatus(categoryWiseStatus);
  const isDraft = isEditMode && initialValues.statusId === draftStatusId;
  const statusDropdownData = getDropdownDataForCategory(
    groupedCategoryStatus,
    CATEGORY_ID_MAPPING.patientTask,
    isDraft
  );

  const handleMultiLanguageChange = (
    languageId: number,
    values: { name: string; description: string }
  ) => {
    setMultiLanguageValues((prevValues) => {
      const updatedValues = { ...prevValues };
      if (!values.name.trim() && !values.description.trim()) {
        delete updatedValues[languageId];
      } else {
        updatedValues[languageId] = values;
      }
      return updatedValues;
    });

    if (languageId && values.name) {
      setSelectedLanguageIds((prevIds) =>
        prevIds.includes(languageId) ? prevIds : [...prevIds, languageId]
      );
    } else if (languageId && !values.name) {
      setSelectedLanguageIds((prevIds) =>
        prevIds.filter((id) => id !== languageId)
      );
    }
  };

  const onCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSaveMilestoneClick}
      mt={3}
      p={2}
    >
      <Grid
        container
        spacing={1}
        mb={2}
      >
        <Grid size={12}>
          <LanguagePills
            activeLanguages={activeLanguages}
            selectedLanguageIds={selectedLanguageIds}
          />
        </Grid>
        <Grid size={3}>
          <CustomTextField
            placeholder={FORM_PLACEHOLDER_TEXTFIELD}
            inputFormTitle={FORM_LABEL_CODE}
            value={formValues.sourceMilestoneCode}
            changeTrigger={(value) => handleInputChange("sourceMilestoneCode", value)}
            disabled={isEditMode}
            isRequired
            // validationRegex={ REGEX_VALIDATIONS.SINGLE_CODE_REGEX }
          />
        </Grid>
        <Grid size={3}>
          <CustomDropdown
            placeholder={FORM_PLACEHOLDER_MILESTONE_TYPE_DROPDOWN}
            inputFormTitle={FORM_LABEL_TYPE}
            value={formValues.milestoneTypeId}
            changeTrigger={(value) => handleInputChange("milestoneTypeId", value)}
            options={formattedTaskTypes}
            isRequired
            disabled={isEditMode}
          />
        </Grid>
        <Grid size={5}>
          <CustomTextField
            placeholder={FORM_PLACEHOLDER_TEXTFIELD}
            inputFormTitle={FORM_LABEL_NAME}
            value={formValues.milestoneDisplayName}
            changeTrigger={(value) => handleInputChange("milestoneDisplayName", value)}
            isRequired
            maxLength={64}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        mb={2}
      >
        <Grid size={3}>
          <CustomPointsRange
            min={0}
            max={150}
            step={25}
            value={formValues.rewardPoints}
            onChange={(value) => handleInputChange("rewardPoints", value)}
            label={FORM_LABEL_POINTS_RANGE}
            isRequired
          />
        </Grid>
        <Grid size={3}>
          <CustomDropdown
            placeholder={FORM_PLACEHOLDER_TRIMESTER_DROPDOWN}
            inputFormTitle={FORM_LABEL_TRIMESTERORGENERAL}
            value={formValues.displayGroupId}
            changeTrigger={(value) =>
              handleInputChange("displayGroupId", value)
            }
            options={[]}
            groupedOptions={groupedOptions}
            disabled={isMilestoneInUse}
            isRequired
          />
        </Grid>
        {isEditMode && (
          <Grid size={3}>
            <CustomDropdown
              inputFormTitle={FORM_LABEL_STATUS}
              value={formValues.statusId}
              changeTrigger={(value) => handleInputChange("statusId", value)}
              options={statusDropdownData}
              isRequired
            />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        spacing={1}
        mb={2}
        alignItems={"center"}
      >
        <Grid size={3}>
          <CustomDropdown
            placeholder={FORM_PLACEHOLDER_MILESTONE_TYPE_DROPDOWN}
            inputFormTitle={FORM_LABEL_CONTENT_TYPE}
            value={formValues.educationalContentTypeId}
            changeTrigger={(value) =>
              handleInputChange("educationalContentTypeId", value)
            }
            options={formattedContentType}
            isRequired
            disabled={isEditMode}
          />
        </Grid>
        <Grid size={5}>
          <CustomTextField
            placeholder={FORM_PLACEHOLDER_TEXTFIELD}
            inputFormTitle={
              formValues.educationalContentTypeId ===
              getIdFromEducationContentType(contentTypes, "URL")
                ? FORM_LABEL_URL_LINK
                : FORM_LABEL_LEARNING_CONTENT_ID
            }
            value={
              formValues.educationalContentTypeId ===
              getIdFromEducationContentType(contentTypes, "URL")
                ? formValues.milestoneLearningUrls
                : formValues.sourceMilestoneCode
            }
            changeTrigger={(value) =>
              handleInputChange(
                formValues.educationalContentTypeId ===
                  getIdFromEducationContentType(contentTypes, "URL")
                  ? "milestoneLearningUrls"
                  : "sourceMilestoneCode",
                value
              )
            }
            disabled={isEditMode}
            isRequired
          />
        </Grid>
        <Grid
          size={2}
          ml={2}
          mt={1}
        >
          <VerifyLink
            key={`${formValues.educationalContentTypeId}-${formValues.milestoneLearningUrls}-${formValues.sourceMilestoneCode}`}
            disabled={
              (formValues.educationalContentTypeId ===
                getIdFromEducationContentType(contentTypes, "URL") &&
                !formValues.milestoneLearningUrls) ||
              (formValues.educationalContentTypeId !==
                getIdFromEducationContentType(contentTypes, "URL") &&
                !formValues.sourceMilestoneCode) ||
              !formValues.educationalContentTypeId
            }
            handleContentVerified={handleContentVerified}
            contentType={formValues.educationalContentTypeId}
            contentId={
              formValues.educationalContentTypeId ===
              getIdFromEducationContentType(contentTypes, "URL")
                ? formValues.milestoneLearningUrls
                : formValues.sourceMilestoneCode
            }
            subs="English"
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        mb={2}
      >
        <Grid size={11}>
          <CustomTextField
            placeholder={FORM_PLACEHOLDER_EDUCATION_DESCRIPTION}
            inputFormTitle={FORM_LABEL_DESCRIPTION}
            value={formValues.description}
            changeTrigger={(value) => handleInputChange("description", value)}
            multi
            rows={4}
            maxLength={512}
            isCharacterCount
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        mb={3}
        mt={4}
      >
        <Grid>
          <Typography
            variant="h3"
            sx={{ fontWeight: "500", lineHeight: "18.75px" }}
          >
            {MULTI_LANGUAGE_SUPPORT}
          </Typography>
        </Grid>
        <Grid>
          <Typography
            variant="body2"
            sx={{ color: "#F40009" }}
          >
            {MULTI_LANGUAGE_SUPPORT_NOTE}
          </Typography>
        </Grid>
      </Grid>
      {activeLanguages
        .filter((language) => language.languageId !== 1)
        .map((language, index) => (
          <>
            <MultiLanguageForm
              key={language.languageId}
              name={multiLanguageValues[language.languageId]?.name || ""}
              description={
                multiLanguageValues[language.languageId]?.description || ""
              }
              language={language.languageName}
              onChange={(values) =>
                handleMultiLanguageChange(language.languageId, values)
              }
            />
            {index < activeLanguages.length - 2 && (
              <Divider
                sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }}
              />
            )}
          </>
        ))}

      <Box
        display="flex"
        justifyContent="flex-start"
        gap={2}
      >
        <CustomButton
          variant="outlined"
          onClick={handleCancel}
          text={BTN_CANCEL}
        />
        <>
          <CustomButton
            variant="outlined"
            onClick={handleSaveDraft}
            text={BTN_SAVE_DRAFT}
            disabled={isEditMode && formValues.statusId !== draftStatusId}
          />
          <CustomButton
            type="submit"
            variant="contained"
            color="primary"
            text={BTN_SAVE_MILESTONE}
            disabled={isEditMode && formValues.statusId === draftStatusId}
            onClick={handleSaveMilestoneClick}
          />
        </>
      </Box>
      <ConfirmationModal
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        handleSave={handleSubmit}
      />
    </Box>
  );
};

export default EducationContentForm;
